import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  MessageCircle,
  Link,
  DollarSign,
  Video,
  FileText,
  ExternalLink,
  AlertCircle,
  Check,
  Lightbulb,
  XCircle,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import TeamResponses from './TeamResponses';
import ResponseButtons from './ResponseButtons';
import CommentModal from './CommentModal';
import FeatureConfirmationDialog from './FeatureConfirmationDialog';
import DependencyIndicator from './DependencyIndicator';
import AdminActions from './AdminActions';
import AdminComments from './AdminComments';
import DialogManager from './DialogManager';
import SupportLinks from './SupportLinks';


// In FeatureRequirementCard.tsx
interface FeatureRequirementCardProps {
  feature: any;
  userResponse: any;
  onResponseChange: (featureRef: string, value: string | null) => Promise<void>;
  comments?: any[];
  onCommentAdd: (featureRef: string, text: string) => void;
  onReactionAdd: (commentId: string, emoji: string) => void;
  reactions?: any[];
  teamResponses?: any[];
  currentUser: any;
  organization: any;
  isExpanded: boolean;
  onToggleExpand: () => void;
  showAllSections?: boolean;
  onNavigateToFeature?: (featureRef: string) => void;
  fetchDependencies: (featureRef: string) => Promise<any[]>;
  fetchConflicts: (featureRef: string) => Promise<any[]>;
  featureDependencies: Record<string, any[]>;
  dependentFeatures: Record<string, any[]>;
  updateDependencyTracking?: () => Promise<void>;
  lastUpdate?: number;
  activeOperations: Set<string>;  // Add this line
}



const FeatureRequirementCard: React.FC<FeatureRequirementCardProps> = ({
  feature,
  userResponse,
  onResponseChange,
  comments = [],
  onCommentAdd,
  onReactionAdd,
  reactions = [],
  teamResponses = [],
  currentUser,
  organization,
  isExpanded,
  onToggleExpand,
  showAllSections = false,
  onNavigateToFeature,
  fetchDependencies,
  fetchConflicts,
  featureDependencies = {},
  dependentFeatures = {},
  updateDependencyTracking,
  lastUpdate,
  activeOperations = new Set()
}) => {

  // First declare all useState hooks
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [dialogQueue, setDialogQueue] = useState<any[]>([]);
  const [pendingResponse, setPendingResponse] = useState<{ featureRef: string; value: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localActiveOperations, setLocalActiveOperations] = useState<Set<string>>(new Set());
  const [localComments, setLocalComments] = useState(comments);

  // Sync with prop updates
useEffect(() => {
  setLocalComments(comments);
}, [comments]);



  // Then declare all useRef hooks
  const pendingResponseRef = useRef<{ featureRef: string; value: string | null } | null>(null);
  const previousValueRef = useRef(userResponse?.response_value);

  // Then compute memoized values
  const systemDependencySource = useMemo(() => {
    return Object.entries(featureDependencies).find(([sourceRef, deps]) =>
      deps.some(dep =>
        dep.required_feature_reference === feature.reference &&
        dep.is_system_dependency
      )
    )?.[0] || null;
  }, [feature.reference, featureDependencies]);

  // Initialize the previousStateRef after systemDependencySource is computed
  const previousStateRef = useRef({
    value: userResponse?.response_value,
    isSystemDep: Boolean(userResponse?.is_system_dependency),
    systemSource: systemDependencySource
  });

  const organizationResponses = useMemo(() =>
    teamResponses.filter(response =>
      response.organization_id === organization?.id
    ), [teamResponses, organization?.id]
  );

  const commentCount = comments.length;

  const getResponseIcon = () => {
    const icons = [];
    
    // Check if this feature is required by other features
    const systemDependencies = dependentFeatures[feature.reference]?.filter(dep => dep.is_required);
    const isSystemDependency = systemDependencies && systemDependencies.length > 0;
  
    // Add system dependency icon first if applicable
    if (isSystemDependency) {
      icons.push(
        <Link 
          key="dependency" 
          className="w-5 h-5 text-blue-600" 
          title={`Required by: ${systemDependencies.map(dep => dep.name).join(', ')}`}
        />
      );
    }
  
    // Add response status icon if exists
    switch (userResponse?.response_value) {
      case 'required':
        icons.push(<Check key="status" className="w-5 h-5 text-green-600" title="Required" />);
        break;
      case 'nice_to_have':
        icons.push(<Lightbulb key="status" className="w-5 h-5 text-blue-600" title="Nice to Have" />);
        break;
      case 'not_needed':
        icons.push(<XCircle key="status" className="w-5 h-5 text-gray-600" title="Not Needed" />);
        break;
      case 'dont_know':
        icons.push(<HelpCircle key="status" className="w-5 h-5 text-amber-500" title="Don't Know" />);
        break;
    }
  
    // If we have any icons, wrap them in a container
    return icons.length > 0 ? (
      <div className="flex items-center gap-2">
        {icons}
      </div>
    ) : null;
  };
  

  // Response handler function with proper dependency checking
  // Response handler function with proper dependency checking
  // Response handler
  // In FeatureRequirementCard.tsx

  // Update socket handler
  const handleSocketUpdate = useCallback((data) => {
    if (!data.teamResponses || activeOperations.has(data.feature_reference)) {
      console.log('[DEBUG] Skipping socket update - active operation in progress');
      return;
    }

    console.log('Processing socket update:', data);
    // Rest of the handler remains the same
  }, [activeOperations]);



  const handleResponseChange = useCallback(async (featureReference: string, value: string | null) => {
    if (!featureReference || !organization?.id) {
        console.error('[DEBUG] Invalid request parameters:', { featureReference, organizationId: organization?.id });
        return false;
    }

    try {
        console.log('[DEBUG] Starting response change:', {
            featureReference,
            value,
            organizationId: organization.id,
            currentResponse: userResponse
        });

        // Only check dependencies when changing FROM required TO something else
        if (userResponse?.response_value === 'required' && value !== 'required') {
            try {
                console.log('[DEBUG] Attempting to unset required feature:', featureReference);

                // Fetch the dependency chain for unsetting
                const chainResponse = await fetch(
                    `/api/features/${featureReference}/unset-chain?` +
                    new URLSearchParams({
                        organization_id: organization.id,
                        user_id: currentUser.id
                    })
                );

                if (!chainResponse.ok) {
                    console.error('[DEBUG] Failed to check dependencies:', await chainResponse.text());
                    // If we can't check dependencies, proceed with the change
                    await onResponseChange(featureReference, value);
                    return true;
                }

                const dependencyChain = await chainResponse.json();
                const systemDependencies = dependencyChain.filter(dep => dep.is_system_dependency);

                if (systemDependencies.length > 0) {
                    setDialogQueue([{
                        type: 'dependency-review',
                        feature: feature,
                        dependencies: systemDependencies,
                        isUnset: true,
                        message: 'Changing this feature will remove the following system dependencies:'
                    }]);
                    setPendingResponse({ featureRef: featureReference, value });
                    pendingResponseRef.current = { featureRef: featureReference, value };
                    return false;
                }
            } catch (error) {
                console.error('[DEBUG] Error checking dependencies:', error);
                // If dependency check fails, proceed with the change
                await onResponseChange(featureReference, value);
                return true;
            }
        }

        // Only check conflicts when setting TO required
        if (value === 'required') {
            try {
                console.log('[DEBUG] Checking conflicts for required status...');
                const conflictsResponse = await fetch(
                    `/api/features/${featureReference}/active-conflicts?` +
                    new URLSearchParams({
                        user_id: currentUser.id,
                        organization_id: organization.id
                    })
                );

                if (!conflictsResponse.ok) {
                    console.error('[DEBUG] Failed to check conflicts:', await conflictsResponse.text());
                    // If we can't check conflicts, proceed with the change
                    await onResponseChange(featureReference, value);
                    return true;
                }

                const activeConflicts = await conflictsResponse.json();

                if (activeConflicts.length > 0) {
                    console.log('[DEBUG] Conflicts found:', activeConflicts);
                    setDialogQueue([{
                        type: 'conflict',
                        feature,
                        conflicts: activeConflicts,
                        message: 'Cannot set as Required - conflicts with these Required features:'
                    }]);
                    setPendingResponse({ featureRef: featureReference, value });
                    pendingResponseRef.current = { featureRef: featureReference, value };
                    return false;
                }

                // Check for dependencies
                console.log('[DEBUG] Checking dependencies...');
                const deps = await fetchDependencies(featureReference);

                if (deps?.length > 0) {
                    console.log('[DEBUG] Dependencies found, showing dialog');
                    setDialogQueue([{
                        type: 'dependency',
                        feature,
                        dependencies: deps,
                        message: 'Setting this feature as Required will mark the following as system dependencies:'
                    }]);
                    setPendingResponse({ featureRef: featureReference, value });
                    pendingResponseRef.current = { featureRef: featureReference, value };
                    return false;
                }
            } catch (error) {
                console.error('[DEBUG] Error checking conflicts/dependencies:', error);
                // If checks fail, proceed with the change
                await onResponseChange(featureReference, value);
                return true;
            }
        }

        // If we get here, all checks passed or were skipped, make the actual change
        await onResponseChange(featureReference, value);
        return true;

    } catch (error) {
        console.error('[ERROR] Error in handleResponseChange:', error);
        setError('Failed to update response');
        throw error;
    }
}, [
    feature,
    userResponse,
    currentUser,
    organization,
    onResponseChange,
    fetchDependencies,
    setPendingResponse,
    pendingResponseRef,
    setDialogQueue
]);



// Update the handleQuickComment function:
const handleQuickComment = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    const text = e.currentTarget.value.trim();
    if (text) {
      // Create temporary comment
      const tempComment = {
        id: `temp-${Date.now()}`,
        user_id: currentUser.id,
        user_name: currentUser.name,
        comment_text: text,
        created_at: new Date().toISOString()
      };

      // Update local state immediately
      setLocalComments(prev => [...prev, tempComment]);
      setNewComment('');

      try {
        // Make API call directly
        const response = await fetch('/api/comments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            feature_reference: feature.reference,
            comment_text: text,
            organization_id: organization.id
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add comment');
        }

        const savedComment = await response.json();
        
        // Replace temp comment with saved one
        setLocalComments(prev => 
          prev.map(comment => 
            comment.id === tempComment.id ? {
              ...savedComment,
              user_name: currentUser.name
            } : comment
          )
        );

        // Still call parent handler
        onCommentAdd(feature.reference, text);
      } catch (error) {
        console.error('Error adding comment:', error);
        // Remove temp comment on failure
        setLocalComments(prev => 
          prev.filter(comment => comment.id !== tempComment.id)
        );
      }
    }
  }
};


// Inside FeatureRequirementCard.tsx - replace the useEffect

useEffect(() => {
  const refreshFeatureState = async () => {
    if (!organization?.id || isRefreshing) return;

    const isActiveOp = activeOperations.has(feature.reference);
    const isSystemDep = userResponse?.is_system_dependency || Boolean(systemDependencySource);

    // Check if anything actually changed
    const prevState = previousStateRef.current;
    const hasChanged = prevState.value !== userResponse?.response_value ||
      prevState.isSystemDep !== isSystemDep ||
      prevState.systemSource !== systemDependencySource;

    if (!hasChanged) {
      return;
    }

    // Skip refresh for non-involved features
    if (activeOperations.size > 0 && !isActiveOp && !isSystemDep) {
      return;
    }

    console.log('[DEBUG] Feature state refresh:', {
      feature: feature.reference,
      isActiveOp,
      isSystemDep,
      systemSource: systemDependencySource,
      hasChanged
    });

    // Update state tracking
    previousStateRef.current = {
      value: userResponse?.response_value,
      isSystemDep,
      systemSource: systemDependencySource
    };

    if (isActiveOp) {
      console.log('[DEBUG] Skipping refresh - active operation:', feature.reference);
      return;
    }

    // Only sync system dependencies if needed
    if (isSystemDep && systemDependencySource) {
      if (!userResponse || userResponse.response_value !== null) {
        console.log('[DEBUG] Syncing system dependency state:', feature.reference);
        try {
          setIsRefreshing(true);
          await onResponseChange(feature.reference, null, true);
        } catch (error) {
          console.log('[DEBUG] Could not sync system dependency - may be required:', error);
        } finally {
          setIsRefreshing(false);
        }
      }
    }
  };

  refreshFeatureState();
}, [
  userResponse?.response_value,
  userResponse?.is_system_dependency,
  feature.reference,
  organization?.id,
  systemDependencySource,
  activeOperations,
  isRefreshing,
  onResponseChange // Add this dependency
]);


  // Add dependency status display
  const renderDependencyStatus = () => {
    if (!featureDependencies[feature.reference]) return null;

    const systemDeps = featureDependencies[feature.reference].filter(
      dep => dep.is_system_dependency
    );

    if (systemDeps.length === 0) return null;

    return (
      <div className="mt-2 text-sm text-blue-600">
        <span className="font-medium">System Dependencies: </span>
        {systemDeps.map((dep, idx) => (
          <span key={dep.required_feature_reference}>
            {idx > 0 && ", "}
            {dep.name}
          </span>
        ))}
      </div>
    );
  };

  const systemDependencyInfo = useMemo(() => {
    const deps = featureDependencies[feature.reference] || [];
    const systemDeps = deps.filter(dep => dep.is_system_dependency);

    if (systemDeps.length === 0) return null;

    return {
      dependencies: systemDeps.map(dep => ({
        required_feature_reference: dep.required_feature_reference,
        name: dep.name,
        is_system_dependency: dep.is_system_dependency,
      })),
      message: (
        <>
          <span key="prefix" className="text-gray-900">This feature requires: </span>
          {systemDeps.map((dep, index) => (
            <React.Fragment key={`dep-${dep.required_feature_reference || index}`}>
              <button
                onClick={() => onNavigateToFeature?.(dep.required_feature_reference)}
                className="text-blue-600 hover:underline"
              >
                {dep.name}
              </button>
              {index < systemDeps.length - 1 && <span key={`sep-${index}`}>, </span>}
            </React.Fragment>
          ))}
        </>
      ),
    };
  }, [feature.reference, featureDependencies, onNavigateToFeature]);

  const getDependencyMessage = () => {
    if (!dependentFeatures[feature.reference]) return null;

    const requiringFeatures = dependentFeatures[feature.reference]
      .filter(dep => dep.is_required);

    if (requiringFeatures.length === 0) return null;

    return (
      <div className="mb-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Link className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">
              Required Dependency
            </h4>
            <p className="text-sm text-blue-700 mb-2">
              This feature is required by other selected features and cannot be modified.
              Required by:
            </p>
            <ul className="space-y-1 ml-4">
              {requiringFeatures.map(dep => (
                <li key={dep.reference} className="text-sm">
                  <button
                    onClick={() => onNavigateToFeature?.(dep.reference)}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {dep.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const LatestComments = ({ comments = [], currentUser }: { comments: any[], currentUser: any }) => {
    if (!currentUser || !comments?.length) return null;

    const sortedComments = [...comments]
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .slice(-2);

    return (
      <div className="space-y-3 mb-4">
        {sortedComments.map((comment) => (
          <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {comment.user_name?.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium">{comment.user_name}</span>
              <span className="text-xs text-gray-500">
                {new Date(comment.created_at).toLocaleString()}
              </span>
            </div>
            <p className="text-sm text-gray-700 ml-10">{comment.comment_text}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      id={`feature-${feature.reference}`}
      data-feature-reference={feature.reference}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
    >
      <div onClick={onToggleExpand} className="flex justify-between items-center cursor-pointer">
        <div className="flex items-center gap-2">
          {getResponseIcon()}
          <h3 className="text-base font-medium text-gray-900">{feature.name}</h3>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          {feature.cost_indicator && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
              Cost: {feature.cost_indicator}
            </span>
          )}
          {feature.reference && <span>Ref: {feature.reference}</span>}
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            <span>{localComments.length} {localComments.length === 1 ? 'comment' : 'comments'}</span>
          </div>
          <div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          <p className="mt-4 text-sm text-gray-600 mb-4">{feature.description}</p>

          {/* Support Links */}
          <div className="mb-4">
            <SupportLinks
              docUrl={feature.support_doc_url}
              videoUrl={feature.support_video_url}
            />
          </div>

          {/* Warning Message */}
          {feature.warning_message && (
            <div className="mb-4 bg-amber-50 p-4 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <p className="text-sm text-amber-700">{feature.warning_message}</p>
              </div>
            </div>
          )}

          {systemDependencyInfo && (!featureDependencies[feature.reference]?.length || userResponse?.response_value !== 'required') && (
            <div className="mt-4 mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-center gap-2">
                <Link className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-blue-900">System Dependencies Required</p>
                  <p className="text-sm text-blue-700 mt-1">{systemDependencyInfo.message}</p>
                </div>
              </div>
            </div>
          )}

          {featureDependencies[feature.reference]?.length > 0 && (
            <div className="mb-4">
              <DependencyIndicator
                feature={feature}
                userResponse={userResponse}
                teamResponses={teamResponses}
                featureDependencies={featureDependencies}
                dependentFeatures={dependentFeatures}
                onNavigateToFeature={onNavigateToFeature}
                organization={organization}
              />
            </div>
          )}

          <AdminActions
            featureRef={feature.reference}
            organization={organization}
            currentUser={currentUser}
            className="mb-4"
          />

          <AdminComments
            featureRef={feature.reference}
            organization={organization}
            isAdmin={currentUser?.is_admin}
            className="mb-4"
          />

          <div className="space-y-4 mb-4">
            {getDependencyMessage()}
            <div className="pt-4">
              <ResponseButtons
                userResponse={userResponse}
                onResponseChange={handleResponseChange}
                disabled={dependentFeatures[feature.reference]?.some(dep => dep.is_required)}
                systemDependency={systemDependencyInfo}
                featureReference={feature.reference}
              />
            </div>
          </div>

          <div className="mb-6">
            <TeamResponses
              responses={organizationResponses}
              featureRef={feature.reference}
              organization={organization}
            />
          </div>

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  Comments ({localComments.length})
                </span>
              </div>
              {commentCount > 0 && (
                <button
                  onClick={() => setIsCommentModalOpen(true)}
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  View all comments
                </button>
              )}
            </div>

            <LatestComments
              comments={localComments}
              currentUser={currentUser}
            />

            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleQuickComment}
              placeholder="Add a comment... (Press Enter to submit, Shift+Enter for new line)"
              className="w-full px-4 py-2 mt-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows={1}
            />
          </div>
        </>
      )}

{isCommentModalOpen && (
  <CommentModal
    isOpen={isCommentModalOpen}
    onClose={() => setIsCommentModalOpen(false)}
    feature={feature}
    comments={localComments}  // Use local state here too
    onCommentAdd={onCommentAdd}
    onReactionAdd={onReactionAdd}
    reactions={reactions}
    currentUser={currentUser}
    featureRef={feature.reference}
    teamResponses={teamResponses}
    userResponse={userResponse}
    onResponseChange={onResponseChange}
    organization={organization}
  />
)}



      {/* Add DialogManager here */}
      <DialogManager
        onResponseChange={onResponseChange}
        dialogQueue={dialogQueue}
        setDialogQueue={setDialogQueue}
        pendingResponse={pendingResponse}
        setPendingResponse={setPendingResponse}
        pendingResponseRef={pendingResponseRef}
        updateDependencyTracking={updateDependencyTracking}
        onNavigateToFeature={onNavigateToFeature}
      />

    </div>
  );
};

export default FeatureRequirementCard;